import { useState } from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ChevronLeft, ChevronRight, SlidersHorizontal } from "lucide-react";
import { DataTable } from "@/components/ui/DataTable/DataTable";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Combobox } from "@/components/ui/combobox";
import { ComboboxMulti } from "@/components/ui/combobox_mutli";
import { CommonDestinationTimezone } from "@/lib/api/utils";
import {
  useListAgtechTickets,
  flattenTickets,
  type FlattenedTicketRow,
  type AgtechTicketsFilters,
} from "@/lib/api/agtech/tickets";
import { useListLocations, type Location } from "@/lib/api/generalContract";
import { useListCustomers } from "@/lib/api/customer";
import { type Customer } from "@/lib/api/reports";
import { CommodityOptions } from "@/lib/api/commodity";
import type { DataTableColumn } from "@/components/ui/DataTable/types";

const DEFAULT_PAGE_SIZE = 100;

const columns: DataTableColumn<FlattenedTicketRow>[] = [
  // ── Scale Ticket ─────────────────────────────────
  {
    id: "scale_ticket_id",
    accessorKey: "scale_ticket_id",
    header: "Ticket No",
    enableGrouping: true,
    size: 100,
  },
  {
    id: "location_name",
    accessorKey: "location_name",
    header: "Location",
    size: 150,
  },
  {
    id: "commodity_display_name",
    accessorKey: "commodity_display_name",
    header: "Commodity",
    cell: (info) => (info.getValue() as string | null) ?? "",
    size: 140,
  },
  {
    id: "customer_name",
    accessorKey: "customer_name",
    header: "Customer",
    cell: (info) => (info.getValue() as string | null) ?? "",
    size: 200,
  },
  {
    id: "ship_date",
    accessorKey: "ship_date",
    header: "Ship Date",
    cell: (info) => {
      const val = info.getValue() as string | null;
      if (!val) return "";
      return moment(val).format("YYYY-MM-DD");
    },
    size: 120,
  },
  {
    id: "vehicle_id",
    accessorKey: "vehicle_id",
    header: "Vehicle",
    size: 220,
  },
  {
    id: "reference_code",
    accessorKey: "reference_code",
    header: "Reference",
    size: 120,
  },
  {
    id: "ship_from",
    accessorKey: "ship_from",
    header: "Ship From",
    size: 180,
  },
  {
    id: "ship_to",
    accessorKey: "ship_to",
    header: "Ship To",
    size: 180,
  },
  {
    id: "scale_ticket_gross_weight",
    accessorKey: "scale_ticket_gross_weight",
    header: "Gross",
    cell: (info) => {
      const val = info.getValue() as string | null;
      if (val === null) return "";
      return Number(val).toLocaleString();
    },
    size: 140,
  },
  {
    id: "scale_ticket_tare_weight",
    accessorKey: "scale_ticket_tare_weight",
    header: "Tare",
    cell: (info) => {
      const val = info.getValue() as string | null;
      if (val == null) return "";
      return Number(val).toLocaleString();
    },
    size: 140,
  },
  {
    id: "split_applied_quantity",
    accessorKey: "split_applied_quantity",
    header: "Split Applied",
    cell: (info) => {
      const val = info.getValue() as string | null;
      if (val == null) return "";
      return Number(val).toLocaleString();
    },
    size: 140,
  },
  {
    id: "split_shrink_quantity",
    accessorKey: "split_shrink_quantity",
    header: "Split Shrink",
    cell: (info) => {
      const val = info.getValue() as string | null;
      if (val == null) return "";
      return Number(val).toLocaleString();
    },
    size: 140,
  },
];

interface ApiFilterValues {
  scaleTicketId: string;
  shipDate: string;
  plcId: string;
  commodityId: string;
  customerIds: string[];
}

const DEFAULT_FILTER_VALUES: ApiFilterValues = {
  scaleTicketId: "",
  shipDate: "",
  plcId: "",
  commodityId: "",
  customerIds: [],
};

function formatWeight(val: string | null | undefined): string {
  if (val === null || val === undefined) return "—";
  return Number(val).toLocaleString() + " lbs";
}

function DetailField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-sm">{value || "—"}</span>
    </div>
  );
}

function TicketDetailModal({
  row,
  open,
  onOpenChange,
}: {
  row: FlattenedTicketRow | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  if (!row) return null;

  const netWeight =
    row.scale_ticket_gross_weight !== null && row.scale_ticket_tare_weight !== null
      ? (Number(row.scale_ticket_gross_weight) - Number(row.scale_ticket_tare_weight)).toLocaleString() + " lbs"
      : "—";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>Ticket #{row.scale_ticket_id}</span>
            {row.instance_type && (
              <Badge variant={row.instance_type === "settlement" ? "default" : "secondary"}>
                {row.instance_type}
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* ── Ticket Info ──────────────────────────── */}
        <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
          <h4 className="font-semibold text-sm">Ticket Details</h4>
          <div className="grid grid-cols-2 gap-x-6 gap-y-3">
            <DetailField label="Location" value={row.location_name} />
            <DetailField label="Commodity" value={row.commodity_display_name} />
            <DetailField label="Customer" value={row.customer_name} />
            <DetailField label="Vehicle" value={row.vehicle_id} />
            <DetailField
              label="Ship Date"
              value={row.ship_date ? moment(row.ship_date).format("MMMM D, YYYY") : null}
            />
            <DetailField label="Reference" value={row.reference_code} />
            <DetailField label="Ship From" value={row.ship_from ?? null} />
            <DetailField label="Ship To" value={row.ship_to ?? null} />
            {row.serial_nbr !== null && row.serial_nbr !== undefined && (
              <DetailField label="Serial #" value={String(row.serial_nbr)} />
            )}
          </div>
        </div>

        {/* ── Weights ──────────────────────────────── */}
        <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
          <h4 className="font-semibold text-sm">Weights</h4>
          <div className="grid grid-cols-3 gap-4">
            <div className="flex flex-col items-center gap-1 rounded-md border bg-background p-3">
              <span className="text-xs font-medium text-muted-foreground">Gross</span>
              <span className="text-lg font-semibold">{formatWeight(row.scale_ticket_gross_weight)}</span>
            </div>
            <div className="flex flex-col items-center gap-1 rounded-md border bg-background p-3">
              <span className="text-xs font-medium text-muted-foreground">Tare</span>
              <span className="text-lg font-semibold">{formatWeight(row.scale_ticket_tare_weight)}</span>
            </div>
            <div className="flex flex-col items-center gap-1 rounded-md border bg-background p-3">
              <span className="text-xs font-medium text-muted-foreground">Net</span>
              <span className="text-lg font-semibold">{netWeight}</span>
            </div>
          </div>
        </div>

        {/* ── Split Info ───────────────────────────── */}
        {row.split_sort_order !== null && (
          <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
            <h4 className="font-semibold text-sm">Split Details</h4>
            <div className="grid grid-cols-2 gap-x-6 gap-y-3">
              <DetailField label="Applied" value={formatWeight(row.split_applied_quantity)} />
              <DetailField label="Shrink" value={formatWeight(row.split_shrink_quantity)} />
            </div>
          </div>
        )}

        {/* ── Grades ───────────────────────────────── */}
        {row.grades.length > 0 && (
          <>
            <Separator />
            <div className="rounded-lg border bg-muted/50 p-4 space-y-3">
              <h4 className="font-semibold text-sm">Grades</h4>
              <div className="grid grid-cols-2 gap-2">
                {row.grades.map((grade, idx) => (
                  <div
                    key={`${grade.charge_grade_code}-${idx}`}
                    className="flex items-center justify-between rounded-md border bg-background px-3 py-2"
                  >
                    <span className="text-sm font-medium text-muted-foreground">
                      {grade.charge_grade_code}
                    </span>
                    <span className="text-sm font-semibold">{grade.charge_grade_value}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function AgtechTicketsPage() {
  // ── Applied filter state (drives the API) ────────
  const [appliedFilters, setAppliedFilters] = useState<ApiFilterValues>(DEFAULT_FILTER_VALUES);

  // ── Filter modal state ───────────────────────────
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [draftFilters, setDraftFilters] = useState<ApiFilterValues>(DEFAULT_FILTER_VALUES);

  // ── Detail modal state ───────────────────────────
  const [selectedRow, setSelectedRow] = useState<FlattenedTicketRow | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // ── Pagination state ─────────────────────────────
  const [page, setPage] = useState(1);
  const [pageSize] = useState(DEFAULT_PAGE_SIZE);

  // ── Data for comboboxes ──────────────────────────
  const locationsResult = useListLocations();
  const customersResult = useListCustomers({ minimal: true });

  const locations: Location[] = locationsResult?.data?.data || [];
  const locationOptions = locations.map((loc) => ({
    value: loc.external_id,
    label: loc.long_name,
  }));

  const customers: Customer[] = customersResult?.data?.data || [];
  const customerOptions = customers.map((cust) => ({
    value: cust.id,
    label: cust.long_name,
  }));

  // ── Build filters object ─────────────────────────
  const filters: AgtechTicketsFilters = {
    page,
    page_size: pageSize,
    scale_ticket_inbound_outbound: "I",
    ...(appliedFilters.scaleTicketId ? { scale_ticket_id: appliedFilters.scaleTicketId } : {}),
    ...(appliedFilters.shipDate ? { ship_date: appliedFilters.shipDate } : {}),
    ...(appliedFilters.plcId ? { plc_id: appliedFilters.plcId } : {}),
    ...(appliedFilters.commodityId ? { commodity_id: appliedFilters.commodityId } : {}),
    ...(appliedFilters.customerIds.length > 0 ? { customer_ids: appliedFilters.customerIds.join(",") } : {}),
  };

  // ── Data fetching ────────────────────────────────
  const ticketsResult = useListAgtechTickets(filters);

  const isSuccess = !!ticketsResult?.data?.success;
  const tickets = isSuccess && ticketsResult.data ? ticketsResult.data.data.data : [];
  const pagination = isSuccess && ticketsResult.data ? ticketsResult.data.data.pagination : null;
  const totalPages = pagination?.total_pages ?? 1;
  const totalCount = pagination?.total_count ?? 0;

  // ── Flatten for DataTable ────────────────────────
  const tableData: FlattenedTicketRow[] = flattenTickets(tickets);

  // ── Filter modal handlers ────────────────────────
  const handleOpenFilterModal = () => {
    setDraftFilters({ ...appliedFilters });
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setPage(1);
    setIsFilterModalOpen(false);
  };

  const handleClearFilters = () => {
    setDraftFilters({ ...DEFAULT_FILTER_VALUES });
  };

  // ── Detail modal handler ─────────────────────────
  const handleRowClick = (row: FlattenedTicketRow) => {
    setSelectedRow(row);
    setIsDetailModalOpen(true);
  };

  const hasActiveFilters =
    appliedFilters.scaleTicketId !== "" ||
    appliedFilters.shipDate !== "" ||
    appliedFilters.plcId !== "" ||
    appliedFilters.commodityId !== "" ||
    appliedFilters.customerIds.length > 0;

  return (
    <div className="flex flex-col gap-4 h-full overflow-hidden">
      {/* Filter Button */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={handleOpenFilterModal}>
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          Filters
          {hasActiveFilters && (
            <span className="ml-2 rounded-full bg-primary text-primary-foreground px-2 py-0.5 text-xs">
              Active
            </span>
          )}
        </Button>
        {ticketsResult.isFetching && (
          <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
        )}
      </div>

      {/* Filter Modal */}
      <Dialog open={isFilterModalOpen} onOpenChange={setIsFilterModalOpen}>
        <DialogContent className="sm:max-w-[500px] md:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>API Filters</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="filter-scale-ticket-id" className="text-xs">Scale Ticket ID</Label>
              <Input
                id="filter-scale-ticket-id"
                placeholder="Scale Ticket ID"
                value={draftFilters.scaleTicketId}
                onChange={(e) =>
                  setDraftFilters((prev) => ({ ...prev, scaleTicketId: e.target.value }))
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="filter-ship-date" className="text-xs">Ship Date</Label>
              <Input
                id="filter-ship-date"
                type="date"
                value={draftFilters.shipDate}
                onChange={(e) =>
                  setDraftFilters((prev) => ({ ...prev, shipDate: e.target.value }))
                }
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-xs">Location</Label>
              <Combobox
                options={locationOptions}
                value={draftFilters.plcId}
                onValueChange={(val) =>
                  setDraftFilters((prev) => ({ ...prev, plcId: val }))
                }
                placeholder="Select location..."
                isModal
                dwimOnChange
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-xs">Commodity</Label>
              <Combobox
                options={CommodityOptions}
                value={draftFilters.commodityId}
                onValueChange={(val) =>
                  setDraftFilters((prev) => ({ ...prev, commodityId: val }))
                }
                placeholder="Select commodity..."
                isModal
                dwimOnChange
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label className="text-xs">Customers</Label>
              <ComboboxMulti
                options={customerOptions}
                value={draftFilters.customerIds}
                onValueChange={(val) =>
                  setDraftFilters((prev) => ({ ...prev, customerIds: val }))
                }
                placeholder="Select customers..."
                isModal
                dwimOnChange
                virtualizeList
                className="w-[400px] md:w-[500px]"
                popoverClassName="w-[400px] md:w-[500px]"
              />
            </div>
          </div>

          <div className="flex justify-between pt-2">
            <Button variant="outline" onClick={handleClearFilters}>
              Clear All
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setIsFilterModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleApplyFilters}>
                Apply Filters
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket Detail Modal */}
      <TicketDetailModal
        row={selectedRow}
        open={isDetailModalOpen}
        onOpenChange={setIsDetailModalOpen}
      />

      {/* Table */}
      <div className="flex-1 overflow-hidden">
        <DataTable
          columns={columns}
          data={tableData}
          loading={ticketsResult.isLoading}
          emptyMessage={ticketsResult.isLoading ? "Loading..." : "No tickets found."}
          enableSorting
          enableGrouping
          enableColumnVisibility
          stickyHeaders
          enableColumnPinning
          enableColumnResizing
          enableExport
          enableFiltering
          enableMultiFilter
          enableMultiSelect
          enableMultiSort
          enableVirtualization
          virtualizationRowHeight={65}
          virtualizationOverscan={2}
          defaultCondensed
          useSteppedGroupingExpansion
          rowClickable
          onRowClick={handleRowClick}
        />
      </div>

      {/* Pagination Controls */}
      {!ticketsResult.isLoading && (
        <div className="flex items-center justify-between py-2">
          <div className="text-sm text-muted-foreground">
            Page {page} of {totalPages} | Total: {totalCount} tickets
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
